import axios from "axios";
import Footer from "./Footer";
import NavBar from "./NavBar";
import { jwtDecode } from "jwt-decode";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import InputAdmin from "../components/Inputs/InputAdmin";
import Loading from "./Loading";
import Swal from "sweetalert2";
import {
  Activity,
  CalendarClock,
  CalendarPlus,
  Eye,
  EyeOff,
  FileText,
  InfoIcon,
  LoaderCircle,
  Pencil,
  Power,
  PowerOff,
  RadioTower,
  Receipt,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserCog,
  Users,
  Wifi,
} from "lucide-react";
import BotaoAddAdmin from "../components/Botoes/BotaoAddAdmin";

const API_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:8002"
    : "https://api-eyazs-production-8f74.up.railway.app");

const MODO_AUDITORIA_SUPER = false;
const TEXTO_VALOR_OCULTO = "Oculto";

function DashboardPanel({ title, subtitle, action, children, className = "" }) {
  return (
    <section className={`bg-white border border-slate-200 rounded-md ${className}`}>
      <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function StatCard({ title, value, helper, icon: Icon, tone = "blue" }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700",
    green: "bg-emerald-50 text-emerald-700",
    orange: "bg-orange-50 text-orange-700",
    violet: "bg-violet-50 text-violet-700",
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md px-4 py-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-slate-500">{title}</p>
          <p className="mt-2 text-2xl font-bold leading-none text-slate-900">{value}</p>
          <p className="mt-2 text-xs text-slate-500">{helper}</p>
        </div>
        <div className={`rounded-md p-2 ${tones[tone]}`}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

export default function SuperAdmin() {
  const [admins, setAdmins] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clientesSemRecibo, setClientesSemRecibo] = useState([]);
  const [recibos, setRecibos] = useState([]);
  const [admin, setAdmin] = useState("");
  const [password, setPassword] = useState("");
  const [tipoAdmin, setTipoAdmin] = useState("normal");
  const [limiteDiasAdmin, setLimiteDiasAdmin] = useState(31);
  const [pesquisaAdmin, setPesquisaAdmin] = useState("");
  const [adding, setAdding] = useState(false);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [messageError, setMessageError] = useState("");
  const [idAlternando, setIdAlternando] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [acaoClienteId, setAcaoClienteId] = useState(null);
  const [filtroClientes, setFiltroClientes] = useState("todos");
  const [mostrarSemRecibo, setMostrarSemRecibo] = useState(false);
  const [mostrarValidade, setMostrarValidade] = useState(false);
  const [mostrarLogs, setMostrarLogs] = useState(false);
  const [eventosSistema, setEventosSistema] = useState([]);
  const navigate = useNavigate();

  const getHeaders = useCallback(() => {
    const token = JSON.parse(localStorage.getItem("token"));
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  }, []);

  function registarEvento(texto) {
    setEventosSistema((eventos) => [
      {
        texto,
        data: new Date().toLocaleString("pt-MZ"),
      },
      ...eventos,
    ].slice(0, 8));
  }

  const clienteEstaAtivo = useCallback((cliente) => {
    return !["DESATVADO", "CONGELADO"].includes(cliente.profile) && cliente.disabled !== "true";
  }, []);

  const clienteEstaCongelado = useCallback((cliente) => {
    return (
      cliente.profile === "CONGELADO" ||
      cliente.validadeusuario?.startsWith("CONGELADO:")
    );
  }, []);

  function planoCliente(cliente) {
    return cliente?.name?.trim().toLowerCase() === "adanca"
      ? cliente.profileusuario || "-"
      : cliente.profile || "-";
  }

  function perfilRealCliente(cliente) {
    return cliente.profile && !["DESATVADO", "CONGELADO"].includes(cliente.profile)
      ? cliente.profile
      : cliente.profileusuario || "";
  }

  const diasRestantes = useCallback((cliente) => {
    if (!cliente.token) return null;

    try {
      const dadosToken = jwtDecode(cliente.token);
      if (!dadosToken?.exp) return null;
      return Math.ceil((dadosToken.exp * 1000 - Date.now()) / 86400000);
    } catch (erro) {
      return null;
    }
  }, []);

  function formatarData(dataISO) {
    if (!dataISO) return "-";
    const data = new Date(dataISO);
    if (Number.isNaN(data.getTime())) return "-";
    const dia = String(data.getDate()).padStart(2, "0");
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  function formatarMoeda(valor) {
    if (MODO_AUDITORIA_SUPER) return TEXTO_VALOR_OCULTO;
    return `${Number(valor || 0).toLocaleString("pt-MZ")} MZN`;
  }

  function escaparHtml(valor) {
    return String(valor || "").replace(/[&<>"']/g, (caractere) => {
      const mapa = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      };
      return mapa[caractere];
    });
  }

  function primeiroValorDisponivel(...valores) {
    return valores.find((valor) => valor !== undefined && valor !== null && String(valor).trim() !== "") || "";
  }

  function obterCampo(objeto, campos) {
    if (!objeto || typeof objeto !== "object") return "";
    for (const campo of campos) {
      if (objeto[campo] !== undefined && objeto[campo] !== null && String(objeto[campo]).trim() !== "") {
        return objeto[campo];
      }
    }
    return "";
  }

  function normalizarTelefoneMpesa(numero) {
    const apenasDigitos = String(numero || "").replace(/\D/g, "");
    if (apenasDigitos.startsWith("258") && apenasDigitos.length === 12) {
      return apenasDigitos.slice(3);
    }
    return apenasDigitos || String(numero || "");
  }

  function dadosUltimaTransacao(cliente) {
    const transacao =
      cliente.ultimaTransacao ||
      cliente.ultima_transacao ||
      cliente.ultimoPagamento ||
      cliente.ultimo_pagamento ||
      cliente.ultimoPagamentoMpesa ||
      cliente.pagamento ||
      {};

    const telefone = primeiroValorDisponivel(
      obterCampo(transacao, ["numeroTelefone", "telefone", "contacto", "numero", "msisdn", "phone"]),
      obterCampo(cliente, ["numeroTelefone", "telefonePagamento", "ultimoPagamentoTelefone", "ultimoTelefoneMpesa", "msisdn", "contacto"])
    );
    const valor = primeiroValorDisponivel(
      obterCampo(transacao, ["valor", "amount", "montante", "total", "totalPagamento"]),
      obterCampo(cliente, ["valorUltimaTransacao", "ultimoPagamentoValor", "ultimoValorPago", "valorPago", "precousuario"])
    );
    const plano = primeiroValorDisponivel(
      obterCampo(transacao, ["plano", "profile", "profileusuario", "perfil"]),
      cliente.ultimoPagamentoPlano,
      cliente.profileusuario,
      perfilRealCliente(cliente),
      planoCliente(cliente)
    );
    const dataTransacao = primeiroValorDisponivel(
      obterCampo(transacao, ["dataTransacao", "dataPagamento", "createdAt", "updatedAt", "data"]),
      obterCampo(cliente, ["dataUltimaTransacao", "ultimoPagamentoData", "ultimoPagamentoEm", "updatedAt"]),
      dataAtualSql()
    );
    const referenciaPagamento = primeiroValorDisponivel(
      obterCampo(transacao, ["referenciaPagamento", "thirdparty_ref", "transaction_ref", "referencia", "reference"]),
      obterCampo(cliente, ["ultimoPagamentoReferencia", "referenciaPagamento"])
    );

    return {
      meioPagamento: primeiroValorDisponivel(
        obterCampo(transacao, ["meioPagamento", "metodoPagamento", "metodo", "paymentMethod"]),
        "Mpesa"
      ),
      numeroTelefone: normalizarTelefoneMpesa(telefone),
      valor,
      plano,
      descricao: `${plano || "Plano"} - pagamento confirmado sem recibo`,
      dataTransacao,
      referenciaPagamento,
    };
  }

  const showAdmins = useCallback(async () => {
    try {
      setLoadingAdmins(true);
      const response = await axios.get(`${API_URL}/admins`);
      if (response?.data) {
        setAdmins(Array.isArray(response.data) ? response.data : []);
      }
    } catch (erro) {
      console.error("Erro ao buscar admins:", erro);
    } finally {
      setLoadingAdmins(false);
    }
  }, []);

  const showDashboard = useCallback(async () => {
    setLoadingDashboard(true);
    try {
      const chamadas = [
        ["clientes", axios.get(`${API_URL}/admin/clientes`, getHeaders())],
        ["semRecibo", axios.get(`${API_URL}/admin/clientes-sem-recibo`, getHeaders())],
        ["recibos", axios.get(`${API_URL}/recibos`, getHeaders())],
      ];
      const resultados = await Promise.allSettled(chamadas.map(([, chamada]) => chamada));
      const [clientesResponse, semReciboResponse, recibosResponse] = resultados;

      if (clientesResponse.status === "fulfilled") {
        setClientes(Array.isArray(clientesResponse.value.data) ? clientesResponse.value.data : []);
      } else {
        console.error("Nao foi possivel carregar clientes do dashboard:", clientesResponse.reason);
      }

      if (semReciboResponse.status === "fulfilled") {
        setClientesSemRecibo(
          Array.isArray(semReciboResponse.value.data) ? semReciboResponse.value.data : []
        );
      } else {
        console.error("Nao foi possivel carregar pagamentos sem recibo:", semReciboResponse.reason);
      }

      if (recibosResponse.status === "fulfilled") {
        setRecibos(Array.isArray(recibosResponse.value.data) ? recibosResponse.value.data : []);
      } else {
        console.error("Nao foi possivel carregar recibos:", recibosResponse.reason);
      }
    } finally {
      setLoadingDashboard(false);
    }
  }, [getHeaders]);

  async function atualizarDashboard() {
    await Promise.all([showAdmins(), showDashboard()]);
    registarEvento("Dashboard atualizado manualmente");
  }

  async function AddAdmin() {
    try {
      setAdding(true);
      if (!admin.trim() || !password.trim()) {
        setMessageError("Admin e password nao devem estar vazios.");
        return;
      }

      const limiteNumerico = Number(limiteDiasAdmin);
      if (tipoAdmin === "pleno" && (!Number.isInteger(limiteNumerico) || limiteNumerico <= 0)) {
        setMessageError("Informe um limite de dias valido para o admin pleno.");
        return;
      }

      const response = await axios.post(`${API_URL}/usuario`, {
        name: admin,
        password,
        tipoUsuario: "admin",
        estado: "ativo",
        tipousuario: tipoAdmin,
        validadeusuario: tipoAdmin === "pleno" ? String(limiteNumerico) : null,
      });

      if (response?.data) {
        await showAdmins();
        registarEvento(
          tipoAdmin === "pleno"
            ? `Admin pleno ${admin} criado com limite de ${limiteNumerico} dias`
            : `Admin ${admin} criado no POP local`
        );
        setMessageError("");
        setAdmin("");
        setPassword("");
        setTipoAdmin("normal");
        setLimiteDiasAdmin(31);
      }
    } catch (erro) {
      console.error("Erro ao cadastrar admin:", erro);
      setMessageError("Houve algum erro ao cadastrar o admin.");
    } finally {
      setAdding(false);
    }
  }

  async function removeAdmin(id) {
    try {
      const resultado = await Swal.fire({
        title: "Tens certeza?",
        text: "Este administrador sera removido.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sim, remover",
        cancelButtonText: "Cancelar",
      });

      if (resultado.isConfirmed) {
        setRemovingId(id);
        const response = await axios.delete(`${API_URL}/usuario/${id}`, getHeaders());
        if (response) {
          await showAdmins();
          registarEvento(`Admin #${id} removido`);
          await Swal.fire({
            title: "Removido",
            text: "O administrador foi removido com sucesso.",
            icon: "success",
          });
        }
      }
    } catch (erro) {
      console.error(erro);
      Swal.fire({
        title: "Erro",
        text: "Nao foi possivel remover o administrador.",
        icon: "error",
      });
    } finally {
      setRemovingId(null);
    }
  }

  async function alternarAtivo(id) {
    try {
      setIdAlternando(id);
      const userResponse = await axios.get(`${API_URL}/usuario/${id}`, getHeaders());
      const estadoAtual = userResponse.data.estado;
      const novoEstado = estadoAtual === "ativo" ? "desativo" : "ativo";

      const response = await axios.put(
        `${API_URL}/usuario/${id}`,
        { estado: novoEstado },
        getHeaders()
      );

      if (response.data) {
        await showAdmins();
        registarEvento(`Admin #${id} ficou ${novoEstado}`);
      }
    } catch (erro) {
      console.error("Erro ao alternar estado:", erro);
    } finally {
      setIdAlternando(null);
    }
  }

  async function alterarEstadoCliente(cliente, estado) {
    const acao = estado === "ativo" ? "ativar" : "desativar";

    try {
      const resultado = await Swal.fire({
        title: `${acao.charAt(0).toUpperCase() + acao.slice(1)} cliente?`,
        text: `Cliente: ${cliente.name}`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: `Sim, ${acao}`,
        cancelButtonText: "Cancelar",
      });

      if (!resultado.isConfirmed) return;

      setAcaoClienteId(`${cliente.idUsuario}-${estado}`);
      await axios.put(
        `${API_URL}/admin/cliente/${cliente.idUsuario}/estado`,
        { estado },
        getHeaders()
      );
      await showDashboard();
      registarEvento(`Cliente ${cliente.name} ${estado === "ativo" ? "ativado" : "desativado"}`);
      await Swal.fire({
        title: "Concluido",
        text: `Cliente ${estado === "ativo" ? "ativado" : "desativado"} com sucesso.`,
        icon: "success",
      });
    } catch (erro) {
      console.error("Erro ao alterar estado do cliente:", erro);
      Swal.fire({
        title: "Erro",
        text: erro.response?.data?.mensagem || "Nao foi possivel alterar o cliente.",
        icon: "error",
      });
    } finally {
      setAcaoClienteId(null);
    }
  }

  async function adicionarDiasCliente(cliente) {
    const planoPadraoCalculado = planoCliente(cliente);
    const planoPadrao =
      planoPadraoCalculado !== "DESATVADO" && planoPadraoCalculado !== "-"
        ? planoPadraoCalculado
        : "";
    const planoPadraoSeguro = escaparHtml(planoPadrao);

    const resultado = await Swal.fire({
      title: `Adicionar dias para ${cliente.name}`,
      html: `
        <div style="display:flex;flex-direction:column;gap:10px;text-align:left">
          <label>Dias</label>
          <input id="diasCliente" type="number" min="1" value="31" class="swal2-input" style="margin:0;width:100%" />
          <label>Plano que o cliente ira receber</label>
          <input id="planoCliente" value="${planoPadraoSeguro}" class="swal2-input" style="margin:0;width:100%" />
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      focusConfirm: false,
      preConfirm: () => {
        const dias = Number(document.getElementById("diasCliente").value);
        const plano = document.getElementById("planoCliente").value.trim();

        if (!Number.isInteger(dias) || dias <= 0) {
          Swal.showValidationMessage("Informe uma quantidade de dias valida.");
          return false;
        }

        if (!plano) {
          Swal.showValidationMessage("Informe o plano que o cliente ira receber.");
          return false;
        }

        return {
          dias,
          plano,
          criarRecibo: false,
        };
      },
    });

    if (!resultado.isConfirmed) return;

    try {
      setAcaoClienteId(`${cliente.idUsuario}-dias`);
      await axios.put(
        `${API_URL}/admin/cliente/${cliente.idUsuario}/adicionar-dias`,
        resultado.value,
        getHeaders()
      );
      await showDashboard();
      registarEvento(
        resultado.value.criarRecibo
          ? `${resultado.value.dias} dias adicionados a ${cliente.name} com recibo`
          : `${resultado.value.dias} dias adicionados a ${cliente.name}`
      );
      await Swal.fire({
        title: "Concluido",
        text: resultado.value.criarRecibo
          ? "Dias adicionados e recibo criado."
          : "Dias adicionados com sucesso.",
        icon: "success",
      });
    } catch (erro) {
      console.error("Erro ao adicionar dias:", erro);
      Swal.fire({
        title: "Erro",
        text: erro.response?.data?.mensagem || "Nao foi possivel adicionar dias.",
        icon: "error",
      });
    } finally {
      setAcaoClienteId(null);
    }
  }

  async function trocarPerfilCliente(cliente) {
    const perfilRealAtual = escaparHtml(perfilRealCliente(cliente));
    const perfilVisivelAtual = escaparHtml(planoCliente(cliente));
    const precoAtual = escaparHtml(cliente.precousuario);
    const servicoAtual = escaparHtml(cliente.service);

    const resultado = await Swal.fire({
      title: `Trocar plano de ${cliente.name}`,
      html: `
        <div style="display:flex;flex-direction:column;gap:10px;text-align:left">
          <label>Perfil real da internet</label>
          <input id="perfilRealCliente" value="${perfilRealAtual}" class="swal2-input" style="margin:0;width:100%" />
          <label>Perfil visivel ao cliente</label>
          <input id="perfilVisivelCliente" value="${perfilVisivelAtual}" class="swal2-input" style="margin:0;width:100%" />
          <label>Preco mensal</label>
          <input id="precoPerfilCliente" type="number" min="0" value="${precoAtual}" class="swal2-input" style="margin:0;width:100%" />
          <label>Servico</label>
          <input id="servicoPerfilCliente" value="${servicoAtual}" class="swal2-input" style="margin:0;width:100%" />
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Atualizar plano",
      cancelButtonText: "Cancelar",
      focusConfirm: false,
      preConfirm: () => {
        const profileOperacional = document.getElementById("perfilRealCliente").value.trim();
        const profileVisivel = document.getElementById("perfilVisivelCliente").value.trim();
        const precousuario = document.getElementById("precoPerfilCliente").value;
        const service = document.getElementById("servicoPerfilCliente").value.trim();

        if (!profileOperacional && !profileVisivel) {
          Swal.showValidationMessage("Informe o perfil real ou o perfil visivel.");
          return false;
        }

        return { profileOperacional, profileVisivel, precousuario, service };
      },
    });

    if (!resultado.isConfirmed) return;

    try {
      setAcaoClienteId(`${cliente.idUsuario}-perfil`);
      await axios.put(
        `${API_URL}/admin/cliente/${cliente.idUsuario}/perfil`,
        resultado.value,
        getHeaders()
      );
      await showDashboard();
      registarEvento(`Perfil de ${cliente.name} atualizado`);
      await Swal.fire({
        title: "Concluido",
        text: "Plano do cliente atualizado com sucesso.",
        icon: "success",
      });
    } catch (erro) {
      console.error("Erro ao trocar perfil:", erro);
      Swal.fire({
        title: "Erro",
        text: erro.response?.data?.mensagem || "Nao foi possivel trocar o perfil.",
        icon: "error",
      });
    } finally {
      setAcaoClienteId(null);
    }
  }

  async function congelarOuDescongelarCliente(cliente) {
    const congelado = clienteEstaCongelado(cliente);
    const acao = congelado ? "descongelar" : "congelar";

    const resultado = await Swal.fire({
      title: `${congelado ? "Descongelar" : "Congelar"} cliente?`,
      text: congelado
        ? "Os dias guardados voltam a contar a partir de agora."
        : "Os dias restantes serao guardados e o cliente fica sem token ativo.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sim, ${acao}`,
      cancelButtonText: "Cancelar",
    });

    if (!resultado.isConfirmed) return;

    try {
      setAcaoClienteId(`${cliente.idUsuario}-${acao}`);
      const response = await axios.put(
        `${API_URL}/admin/cliente/${cliente.idUsuario}/${acao}`,
        {},
        getHeaders()
      );
      await showDashboard();
      registarEvento(
        `${cliente.name} ${congelado ? "descongelado" : "congelado"} com ${response.data?.dias ?? 0} dias`
      );
      await Swal.fire({
        title: "Concluido",
        text: response.data?.mensagem || "Acao concluida com sucesso.",
        icon: "success",
      });
    } catch (erro) {
      console.error("Erro ao congelar/descongelar:", erro);
      Swal.fire({
        title: "Erro",
        text: erro.response?.data?.mensagem || "Nao foi possivel concluir a acao.",
        icon: "error",
      });
    } finally {
      setAcaoClienteId(null);
    }
  }

  async function alterarVisibilidadeCliente(cliente) {
    const ocultar = cliente.estado !== "oculto";
    const resultado = await Swal.fire({
      title: ocultar ? "Ocultar cliente dos admins?" : "Mostrar cliente aos admins?",
      text: ocultar
        ? "O cliente continua no sistema, mas nao aparece nas listas dos admins normais."
        : "O cliente volta a aparecer nas listas dos admins.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: ocultar ? "Sim, ocultar" : "Sim, mostrar",
      cancelButtonText: "Cancelar",
    });

    if (!resultado.isConfirmed) return;

    try {
      setAcaoClienteId(`${cliente.idUsuario}-visibilidade`);
      await axios.put(
        `${API_URL}/admin/cliente/${cliente.idUsuario}/visibilidade`,
        { oculto: ocultar },
        getHeaders()
      );
      await showDashboard();
      registarEvento(`${cliente.name} ficou ${ocultar ? "oculto para admins" : "visivel para admins"}`);
    } catch (erro) {
      console.error("Erro ao alterar visibilidade:", erro);
      Swal.fire({
        title: "Erro",
        text: erro.response?.data?.mensagem || "Nao foi possivel alterar a visibilidade.",
        icon: "error",
      });
    } finally {
      setAcaoClienteId(null);
    }
  }

  function dataAtualSql() {
    const data = new Date();
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const dia = String(data.getDate()).padStart(2, "0");
    const horas = String(data.getHours()).padStart(2, "0");
    const minutos = String(data.getMinutes()).padStart(2, "0");
    const segundos = String(data.getSeconds()).padStart(2, "0");
    return `${ano}-${mes}-${dia} ${horas}:${minutos}:${segundos}`;
  }

  async function criarOuEditarRecibo({ recibo = null, cliente = null } = {}) {
    const editando = Boolean(recibo);
    const clienteDoRecibo = cliente || {};
    const dadosPagamento = dadosUltimaTransacao(clienteDoRecibo);
    const valores = {
      idUsuario: recibo?.idUsuario || clienteDoRecibo.idUsuario || "",
      userName: recibo?.userName || clienteDoRecibo.name || "",
      meioPagamento: recibo?.meioPagamento || dadosPagamento.meioPagamento,
      numeroTelefone: recibo?.numeroTelefone || dadosPagamento.numeroTelefone,
      valor: recibo?.valor || dadosPagamento.valor,
      plano: recibo?.plano || dadosPagamento.plano,
      descricao: recibo?.descricao || dadosPagamento.descricao,
      dataTransacao: recibo?.dataTransacao || dadosPagamento.dataTransacao,
      referenciaPagamento: recibo?.referenciaPagamento || dadosPagamento.referenciaPagamento,
    };

    const resultado = await Swal.fire({
      title: editando ? "Editar recibo" : "Criar recibo",
      html: `
        <div style="display:flex;flex-direction:column;gap:10px;text-align:left">
          <label>ID do cliente</label>
          <input id="reciboIdUsuario" value="${escaparHtml(valores.idUsuario)}" class="swal2-input" style="margin:0;width:100%" />
          <label>Nome do cliente</label>
          <input id="reciboUserName" value="${escaparHtml(valores.userName)}" class="swal2-input" style="margin:0;width:100%" />
          <label>Metodo de pagamento</label>
          <select id="reciboMeioPagamento" class="swal2-input" style="margin:0;width:100%">
            ${["Mpesa", "Emola", "Manual", "Banco", "Cheque"]
              .map((metodo) => `<option value="${metodo}" ${valores.meioPagamento === metodo ? "selected" : ""}>${metodo}</option>`)
              .join("")}
          </select>
          <label>Telefone</label>
          <input id="reciboTelefone" value="${escaparHtml(valores.numeroTelefone)}" class="swal2-input" style="margin:0;width:100%" />
          <label>Valor</label>
          <input id="reciboValor" type="number" min="0" value="${escaparHtml(valores.valor)}" class="swal2-input" style="margin:0;width:100%" />
          <label>Plano</label>
          <input id="reciboPlano" value="${escaparHtml(valores.plano)}" class="swal2-input" style="margin:0;width:100%" />
          <label>Descricao</label>
          <input id="reciboDescricao" value="${escaparHtml(valores.descricao)}" class="swal2-input" style="margin:0;width:100%" />
          <label>Data da transacao</label>
          <input id="reciboData" value="${escaparHtml(valores.dataTransacao)}" class="swal2-input" style="margin:0;width:100%" />
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: editando ? "Guardar" : "Criar recibo",
      cancelButtonText: "Cancelar",
      focusConfirm: false,
      preConfirm: () => {
        const idUsuario = document.getElementById("reciboIdUsuario").value;
        const userName = document.getElementById("reciboUserName").value.trim();
        const valor = Number(document.getElementById("reciboValor").value);
        const plano = document.getElementById("reciboPlano").value.trim();

        if (!idUsuario || !userName || !valor || !plano) {
          Swal.showValidationMessage("ID, nome, valor e plano sao obrigatorios.");
          return false;
        }

        return {
          idUsuario,
          userName,
          meioPagamento: document.getElementById("reciboMeioPagamento").value,
          numeroTelefone: document.getElementById("reciboTelefone").value || "Manual",
          valor,
          plano,
          descricao: document.getElementById("reciboDescricao").value || plano,
          dataTransacao: document.getElementById("reciboData").value || dataAtualSql(),
          referenciaPagamento: valores.referenciaPagamento || undefined,
        };
      },
    });

    if (!resultado.isConfirmed) return;

    try {
      if (editando) {
        await axios.put(`${API_URL}/recibo/${recibo.idRecibo}`, resultado.value, getHeaders());
      } else {
        await axios.post(`${API_URL}/recibo`, resultado.value, getHeaders());
      }
      await showDashboard();
      registarEvento(editando ? `Recibo #${recibo.idRecibo} editado` : `Recibo criado para ${resultado.value.userName}`);
      await Swal.fire({
        title: "Concluido",
        text: editando ? "Recibo atualizado com sucesso." : "Recibo criado com sucesso.",
        icon: "success",
      });
    } catch (erro) {
      Swal.fire({
        title: "Erro",
        text: erro.response?.data?.mensagem || "Nao foi possivel guardar o recibo.",
        icon: "error",
      });
    }
  }

  async function alterarVisibilidadeRecibo(recibo) {
    const ocultar = recibo.estado !== "oculto";

    try {
      await axios.put(
        `${API_URL}/recibo/${recibo.idRecibo}/visibilidade`,
        { oculto: ocultar },
        getHeaders()
      );
      await showDashboard();
      registarEvento(`Recibo #${recibo.idRecibo} ${ocultar ? "ocultado" : "visivel"}`);
    } catch (erro) {
      Swal.fire({
        title: "Erro",
        text: erro.response?.data?.mensagem || "Nao foi possivel alterar o recibo.",
        icon: "error",
      });
    }
  }

  async function apagarRecibo(recibo) {
    const resultado = await Swal.fire({
      title: "Apagar recibo?",
      text: `Recibo #${recibo.idRecibo} de ${recibo.userName}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, apagar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc2626",
    });

    if (!resultado.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}/recibo/${recibo.idRecibo}`, getHeaders());
      await showDashboard();
      registarEvento(`Recibo #${recibo.idRecibo} apagado`);
    } catch (erro) {
      Swal.fire({
        title: "Erro",
        text: erro.response?.data?.mensagem || "Nao foi possivel apagar o recibo.",
        icon: "error",
      });
    }
  }

  useEffect(() => {
    showAdmins();
    showDashboard();
  }, [showAdmins, showDashboard]);

  const clientesSemReciboIds = useMemo(
    () => new Set(clientesSemRecibo.map((cliente) => cliente.idUsuario)),
    [clientesSemRecibo]
  );

  const recibosOrdenados = useMemo(() => {
    return [...recibos].sort(
      (a, b) => new Date(b.dataTransacao || 0) - new Date(a.dataTransacao || 0)
    );
  }, [recibos]);

  const recibosDoMes = useMemo(() => {
    const hoje = new Date();
    return recibos.filter((recibo) => {
      const data = new Date(recibo.dataTransacao);
      return (
        data.getMonth() === hoje.getMonth() &&
        data.getFullYear() === hoje.getFullYear()
      );
    });
  }, [recibos]);

  const receitaMes = useMemo(
    () => recibosDoMes.reduce((total, recibo) => total + Number(recibo.valor || 0), 0),
    [recibosDoMes]
  );

  const receitaAcumulada = useMemo(
    () => recibos.reduce((total, recibo) => total + Number(recibo.valor || 0), 0),
    [recibos]
  );

  const clientesRecentes = useMemo(() => {
    return [...clientes]
      .sort((a, b) => Number(b.idUsuario || 0) - Number(a.idUsuario || 0))
      .slice(0, 6);
  }, [clientes]);

  const clientesValidade = useMemo(() => {
    return clientes
      .map((cliente) => ({ ...cliente, dias: diasRestantes(cliente) }))
      .filter((cliente) => cliente.dias !== null && cliente.dias <= 7)
      .sort((a, b) => a.dias - b.dias);
  }, [clientes, diasRestantes]);

  const adminsFiltrados = useMemo(() => {
    return admins.filter((item) =>
      item.name?.toLowerCase().includes(pesquisaAdmin.toLowerCase())
    );
  }, [admins, pesquisaAdmin]);

  const clientesFiltrados = useMemo(() => {
    if (filtroClientes === "ativos") {
      return clientes.filter(clienteEstaAtivo);
    }

    if (filtroClientes === "desativos") {
      return clientes.filter((cliente) => !clienteEstaAtivo(cliente));
    }

    if (filtroClientes === "semRecibo") {
      return clientes.filter((cliente) => clientesSemReciboIds.has(cliente.idUsuario));
    }

    if (filtroClientes === "validade") {
      return clientesValidade;
    }

    if (filtroClientes === "ocultos") {
      return clientes.filter((cliente) => cliente.estado === "oculto");
    }

    if (filtroClientes === "congelados") {
      return clientes.filter(clienteEstaCongelado);
    }

    return clientes;
  }, [clienteEstaAtivo, clienteEstaCongelado, clientes, clientesSemReciboIds, clientesValidade, filtroClientes]);

  const estatisticas = useMemo(() => {
    const ativos = clientes.filter(clienteEstaAtivo).length;
    return {
      total: clientes.length,
      ativos,
      desativos: clientes.length - ativos,
      semRecibo: clientesSemRecibo.length,
      adminsAtivos: admins.filter((item) => item.estado === "ativo").length,
    };
  }, [admins, clienteEstaAtivo, clientes, clientesSemRecibo]);

  const propsInputAdmin = {
    type: "text",
    placeholder: "digite o nome do Admin",
    mudar: (e) => setAdmin(e.target.value),
    value: admin,
  };

  const propsInputPassword = {
    type: "text",
    placeholder: "digite o Password do Admin",
    mudar: (e) => setPassword(e.target.value),
    value: password,
  };

  const propsBotaoAdd = {
    texto: adding ? (
      <div className="flex justify-center items-center gap-1 my-1 text-center">
        <span className="text-center">Processando</span>
        <LoaderCircle
          strokeWidth={4}
          size={18}
          className={adding ? "animate-spin text-white" : "animate-none"}
        />
      </div>
    ) : (
      "+ Adicionar admin"
    ),
    type: "submit",
  };

  const carregando = loadingDashboard && clientes.length === 0;
  const percentagemAtivos =
    estatisticas.total > 0
      ? Math.round((estatisticas.ativos / estatisticas.total) * 100)
      : 0;

  return (
    <div className="flex min-h-screen flex-col bg-slate-100">
      <div className="bg-blue-600 shadow-md">
        <NavBar />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-3 py-4 sm:px-5">
        <div className="mb-4 rounded-md bg-white border border-slate-200 px-4 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold text-blue-700">Super Admin</p>
              <h1 className="text-xl font-bold text-slate-950">Dashboard geral</h1>
              <p className="mt-1 text-xs text-slate-500">
                Visao geral de clientes, pagamentos e administradores do POP local.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={atualizarDashboard}
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                <RefreshCw size={14} />
                Atualizar
              </button>
              <button
                onClick={() => navigate("/admin")}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
              >
                <UserCog size={14} />
                Area admin
              </button>
            </div>
          </div>
        </div>

        {carregando ? (
          <Loading />
        ) : (
          <>
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <StatCard
                title="Clientes"
                value={estatisticas.total}
                helper={`${estatisticas.ativos} ativos`}
                icon={Users}
                tone="blue"
              />
              <StatCard
                title="Subscricoes ativas"
                value={estatisticas.ativos}
                helper={`${estatisticas.desativos} terminadas`}
                icon={Wifi}
                tone="green"
              />
              <StatCard
                title="Receita do mes"
                value={formatarMoeda(receitaMes)}
                helper={`${recibosDoMes.length} recibos`}
                icon={Receipt}
                tone="orange"
              />
              <StatCard
                title="Administradores"
                value={admins.length}
                helper={`${estatisticas.adminsAtivos} ativos`}
                icon={ShieldCheck}
                tone="violet"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[2fr_1fr]">
              <DashboardPanel
                title="Actividade recente"
                subtitle="Ultimos recibos registados no sistema."
                action={<FileText className="text-blue-600" size={16} />}
              >
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="text-slate-500">
                      <tr>
                        <th className="border-b px-2 py-2 text-left">Cliente</th>
                        <th className="border-b px-2 py-2 text-left">Metodo</th>
                        <th className="border-b px-2 py-2 text-left">Valor</th>
                        <th className="border-b px-2 py-2 text-left">Data</th>
                        <th className="border-b px-2 py-2 text-center">Recibo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recibosOrdenados.slice(0, 7).map((recibo) => (
                        <tr key={recibo.idRecibo} className="hover:bg-slate-50">
                          <td className="border-b px-2 py-2 font-semibold text-slate-800">
                            {recibo.userName}
                          </td>
                          <td className="border-b px-2 py-2">{recibo.meioPagamento}</td>
                          <td className="border-b px-2 py-2">{formatarMoeda(recibo.valor)}</td>
                          <td className="border-b px-2 py-2">{formatarData(recibo.dataTransacao)}</td>
                          <td className="border-b px-2 py-2 text-center">
                            <button
                              onClick={() => navigate(`/recibo/${recibo.idRecibo}`)}
                              className="rounded border border-blue-200 px-2 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-50"
                            >
                              Abrir
                            </button>
                          </td>
                        </tr>
                      ))}
                      {recibosOrdenados.length === 0 && (
                        <tr>
                          <td colSpan="5" className="py-5 text-center text-slate-500">
                            Ainda nao ha recibos registados.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </DashboardPanel>

              <DashboardPanel
                title="POP local"
                subtitle="Este sistema opera com apenas um POP."
                action={<RadioTower className="text-blue-600" size={16} />}
              >
                <div className="space-y-4">
                  <div>
                    <div className="mb-1 flex justify-between text-xs text-slate-600">
                      <span>Clientes ativos</span>
                      <span>{estatisticas.ativos} / {estatisticas.total}</span>
                    </div>
                    <div className="h-2 rounded bg-slate-100">
                      <div
                        className="h-2 rounded bg-blue-600"
                        style={{ width: `${percentagemAtivos}%` }}
                      />
                    </div>
                  </div>
                  <div className="rounded-md bg-slate-50 p-4">
                    <p className="text-xs text-slate-500">Receita acumulada</p>
                    <p className="mt-2 text-2xl font-bold text-slate-950">
                      {formatarMoeda(receitaAcumulada)}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded border border-slate-200 p-3">
                      <p className="text-slate-500">Sem recibo</p>
                      <p className="mt-1 text-lg font-bold text-yellow-700">
                        {estatisticas.semRecibo}
                      </p>
                    </div>
                    <div className="rounded border border-slate-200 p-3">
                      <p className="text-slate-500">A expirar</p>
                      <p className="mt-1 text-lg font-bold text-red-700">
                        {clientesValidade.length}
                      </p>
                    </div>
                  </div>
                </div>
              </DashboardPanel>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
              <DashboardPanel
                title="Acoes rapidas"
                subtitle="Navegacao operacional."
              >
                <div className="space-y-2">
                  {[
                    ["Ver clientes", "/admin/clientes"],
                    ["Clientes ativos", "/admin/clientes/ativos"],
                    ["Clientes desativos", "/admin/clientes/desativos"],
                    ["Broadcast", "/admin/broadcast"],
                  ].map(([label, path]) => (
                    <button
                      key={path}
                      onClick={() => navigate(path)}
                      className="flex w-full items-center justify-between rounded-md border border-slate-300 px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      {label}
                      <Users size={14} />
                    </button>
                  ))}
                </div>
              </DashboardPanel>

              <DashboardPanel
                title="Criar administrador"
                subtitle="Admin criado para o POP local."
              >
                <div className="space-y-3">
                  {messageError && <p className="text-xs font-semibold text-red-600">{messageError}</p>}
                  <InputAdmin {...propsInputAdmin} />
                  <InputAdmin {...propsInputPassword} />
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <label className="text-xs font-semibold text-slate-700">
                      Tipo
                      <select
                        value={tipoAdmin}
                        onChange={(e) => setTipoAdmin(e.target.value)}
                        className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-xs outline-none focus:border-blue-500"
                      >
                        <option value="normal">Admin normal</option>
                        <option value="pleno">Admin pleno</option>
                      </select>
                    </label>
                    <label className="text-xs font-semibold text-slate-700">
                      Limite de dias
                      <input
                        type="number"
                        min="1"
                        value={limiteDiasAdmin}
                        onChange={(e) => setLimiteDiasAdmin(e.target.value)}
                        disabled={tipoAdmin !== "pleno"}
                        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-xs outline-none focus:border-blue-500 disabled:bg-slate-100 disabled:text-slate-400"
                      />
                    </label>
                  </div>
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                    <p className="font-semibold text-slate-800">Privilegios</p>
                    <label className="mt-2 flex items-center gap-2">
                      <input type="checkbox" checked readOnly />
                      Ver clientes ativos
                    </label>
                    <label className="mt-1 flex items-center gap-2">
                      <input type="checkbox" checked readOnly />
                      Ver clientes desativos
                    </label>
                    <label className="mt-1 flex items-center gap-2">
                      <input type="checkbox" checked={tipoAdmin === "pleno"} readOnly />
                      Admin pleno adiciona dias apenas a clientes desativados
                    </label>
                    <p className="mt-3">
                      Sem campo de POP: todos os admins trabalham no POP local.
                    </p>
                  </div>
                  <BotaoAddAdmin clicado={adding ? () => {} : AddAdmin} {...propsBotaoAdd} />
                </div>
              </DashboardPanel>

              <DashboardPanel
                title="Clientes recentes"
                subtitle="Ultimos registos locais."
                action={<Activity className="text-blue-600" size={16} />}
              >
                <div className="space-y-3">
                  {clientesRecentes.map((cliente) => {
                    const ativo = clienteEstaAtivo(cliente);
                    const congelado = clienteEstaCongelado(cliente);
                    return (
                      <div key={cliente.idUsuario} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{cliente.name}</p>
                          <p className="text-xs text-slate-500">
                            {planoCliente(cliente)}
                          </p>
                        </div>
                        <span className={`text-xs font-semibold ${ativo ? "text-green-600" : congelado ? "text-blue-700" : "text-red-600"}`}>
                          {congelado ? "Congelado" : ativo ? "Activo" : "Terminado"}
                        </span>
                      </div>
                    );
                  })}
                  {clientesRecentes.length === 0 && (
                    <p className="text-center text-xs text-slate-500">Sem clientes recentes.</p>
                  )}
                </div>
              </DashboardPanel>
            </div>

            <div className="mt-4 space-y-4">
              <DashboardPanel
                    title="Pagamentos sem recibo"
                    subtitle="Clientes com pagamento confirmado e recibo ainda nao registado."
                    action={
                      <button
                        onClick={() => setMostrarSemRecibo((valor) => !valor)}
                        className="rounded-md bg-slate-950 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
                      >
                        {mostrarSemRecibo ? "Ocultar pagamentos" : "Mostrar pagamentos"}
                      </button>
                    }
                  >
                    {mostrarSemRecibo && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead className="text-slate-500">
                            <tr>
                              <th className="border-b px-2 py-2 text-left">ID</th>
                              <th className="border-b px-2 py-2 text-left">Cliente</th>
                              <th className="border-b px-2 py-2 text-left">Profile</th>
                              <th className="border-b px-2 py-2 text-left">Telefone usado</th>
                              <th className="border-b px-2 py-2 text-left">Data</th>
                              <th className="border-b px-2 py-2 text-center">Acoes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {clientesSemRecibo.map((cliente) => {
                              const ultimaTransacao = dadosUltimaTransacao(cliente);

                              return (
                                <tr key={cliente.idUsuario}>
                                  <td className="border-b px-2 py-2">{cliente.idUsuario}</td>
                                  <td className="border-b px-2 py-2 font-semibold text-slate-800">{cliente.name}</td>
                                  <td className="border-b px-2 py-2">{ultimaTransacao.plano || planoCliente(cliente)}</td>
                                  <td className="border-b px-2 py-2">{ultimaTransacao.numeroTelefone || "-"}</td>
                                  <td className="border-b px-2 py-2">{formatarData(ultimaTransacao.dataTransacao)}</td>
                                  <td className="border-b px-2 py-2 text-center">
                                    <button
                                      onClick={() => criarOuEditarRecibo({ cliente })}
                                      className="rounded border border-blue-200 px-2 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-50"
                                    >
                                      Gerar recibo
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                            {clientesSemRecibo.length === 0 && (
                              <tr>
                                <td colSpan="6" className="py-4 text-center text-slate-500">
                                  Sem clientes ativos em falta de recibo.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </DashboardPanel>

              {!MODO_AUDITORIA_SUPER && (
                  <DashboardPanel
                    title="Gestao de recibos"
                    subtitle="Criar, editar, ocultar ou apagar recibos."
                    action={
                      <button
                        onClick={() => criarOuEditarRecibo()}
                        className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
                      >
                        <Receipt size={14} />
                        Criar recibo
                      </button>
                    }
                  >
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead className="text-slate-500">
                          <tr>
                            <th className="border-b px-2 py-2 text-left">Cliente</th>
                            <th className="border-b px-2 py-2 text-left">Plano</th>
                            <th className="border-b px-2 py-2 text-left">Valor</th>
                            <th className="border-b px-2 py-2 text-left">Data</th>
                            <th className="border-b px-2 py-2 text-left">Estado</th>
                            <th className="border-b px-2 py-2 text-center">Acoes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recibosOrdenados.slice(0, 10).map((recibo) => {
                            const oculto = recibo.estado === "oculto";
                            return (
                              <tr key={recibo.idRecibo} className="hover:bg-slate-50">
                                <td className="border-b px-2 py-2 font-semibold text-slate-900">
                                  {recibo.userName}
                                </td>
                                <td className="border-b px-2 py-2">{recibo.plano || "-"}</td>
                                <td className="border-b px-2 py-2">{formatarMoeda(recibo.valor)}</td>
                                <td className="border-b px-2 py-2">{formatarData(recibo.dataTransacao)}</td>
                                <td className="border-b px-2 py-2">
                                  <span className={`font-semibold ${oculto ? "text-slate-500" : "text-green-600"}`}>
                                    {oculto ? "Oculto" : "Visivel"}
                                  </span>
                                </td>
                                <td className="border-b px-2 py-2">
                                  <div className="flex flex-wrap justify-center gap-2">
                                    <button
                                      title="Editar recibo"
                                      onClick={() => criarOuEditarRecibo({ recibo })}
                                      className="rounded border border-slate-300 p-2 hover:bg-blue-50 hover:text-blue-700"
                                    >
                                      <Pencil size={15} />
                                    </button>
                                    <button
                                      title={oculto ? "Mostrar recibo" : "Ocultar recibo"}
                                      onClick={() => alterarVisibilidadeRecibo(recibo)}
                                      className="rounded border border-slate-300 p-2 hover:bg-slate-100 hover:text-slate-900"
                                    >
                                      {oculto ? <Eye size={15} /> : <EyeOff size={15} />}
                                    </button>
                                    <button
                                      title="Apagar recibo"
                                      onClick={() => apagarRecibo(recibo)}
                                      className="rounded border border-slate-300 p-2 hover:bg-red-50 hover:text-red-700"
                                    >
                                      <Trash2 size={15} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          {recibosOrdenados.length === 0 && (
                            <tr>
                              <td colSpan="6" className="py-4 text-center text-slate-500">
                                Ainda nao ha recibos registados.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </DashboardPanel>
              )}

              <DashboardPanel
                title="Validade dos clientes"
                subtitle="Dias restantes e possiveis desconexoes."
                action={
                  <button
                    onClick={() => setMostrarValidade((valor) => !valor)}
                    className="inline-flex items-center gap-2 rounded-md bg-slate-950 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
                  >
                    <CalendarClock size={14} />
                    {mostrarValidade ? "Ocultar clientes" : "Mostrar clientes"}
                  </button>
                }
              >
                {mostrarValidade && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="text-slate-500">
                        <tr>
                          <th className="border-b px-2 py-2 text-left">Cliente</th>
                          <th className="border-b px-2 py-2 text-left">Plano</th>
                          <th className="border-b px-2 py-2 text-left">Dias</th>
                          <th className="border-b px-2 py-2 text-center">Acoes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientesValidade.map((cliente) => (
                          <tr key={cliente.idUsuario}>
                            <td className="border-b px-2 py-2 font-semibold text-slate-800">{cliente.name}</td>
                            <td className="border-b px-2 py-2">{planoCliente(cliente)}</td>
                            <td className={`border-b px-2 py-2 font-semibold ${cliente.dias < 0 ? "text-red-700" : "text-yellow-700"}`}>
                              {cliente.dias < 0 ? "Expirado" : `${cliente.dias} dias`}
                            </td>
                            <td className="border-b px-2 py-2 text-center">
                              <button
                                onClick={() => adicionarDiasCliente(cliente)}
                                className="rounded border border-blue-200 px-2 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-50"
                              >
                                Adicionar dias
                              </button>
                            </td>
                          </tr>
                        ))}
                        {clientesValidade.length === 0 && (
                          <tr>
                            <td colSpan="4" className="py-4 text-center text-slate-500">
                              Nenhum cliente perto de expirar.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </DashboardPanel>

              <DashboardPanel
                title="Logs do sistema"
                subtitle="Acoes executadas nesta sessao."
                action={
                  <button
                    onClick={() => setMostrarLogs((valor) => !valor)}
                    className="inline-flex items-center gap-2 rounded-md bg-slate-950 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
                  >
                    <Activity size={14} />
                    {mostrarLogs ? "Ocultar logs" : "Mostrar logs"}
                  </button>
                }
              >
                {mostrarLogs && (
                  <div className="space-y-2 text-xs">
                    {eventosSistema.map((evento, index) => (
                      <div key={`${evento.data}-${index}`} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2">
                        <span className="font-semibold text-slate-800">{evento.texto}</span>
                        <span className="text-slate-500">{evento.data}</span>
                      </div>
                    ))}
                    {eventosSistema.length === 0 && (
                      <p className="text-center text-slate-500">Sem logs nesta sessao.</p>
                    )}
                  </div>
                )}
              </DashboardPanel>
            </div>

            <DashboardPanel
              title="Relatorio de ativacoes"
              subtitle="Dias extras concedidos por admins, com motivo obrigatorio quando registado."
              className="mt-4"
            >
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="border-b px-2 py-2 text-left">Admin</th>
                      <th className="border-b px-2 py-2 text-left">Cliente</th>
                      <th className="border-b px-2 py-2 text-left">POP</th>
                      <th className="border-b px-2 py-2 text-left">Dias</th>
                      <th className="border-b px-2 py-2 text-left">Observacao</th>
                      <th className="border-b px-2 py-2 text-left">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventosSistema.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="py-4 text-center text-slate-500">
                          Sem ativacoes registadas nesta sessao.
                        </td>
                      </tr>
                    ) : (
                      eventosSistema.slice(0, 4).map((evento, index) => (
                        <tr key={`${evento.data}-relatorio-${index}`}>
                          <td className="border-b px-2 py-2 font-semibold">super</td>
                          <td className="border-b px-2 py-2">{evento.texto}</td>
                          <td className="border-b px-2 py-2">POP local</td>
                          <td className="border-b px-2 py-2">-</td>
                          <td className="border-b px-2 py-2">Sessao atual</td>
                          <td className="border-b px-2 py-2">{evento.data}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </DashboardPanel>

            <DashboardPanel
              title="Gestao de administradores"
              subtitle="Ativar, desativar ou remover administradores do POP local."
              className="mt-4"
              action={
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
                  <input
                    value={pesquisaAdmin}
                    onChange={(e) => setPesquisaAdmin(e.target.value)}
                    placeholder="Pesquisar admin..."
                    className="w-56 rounded-md border border-slate-300 py-2 pl-9 pr-3 text-xs outline-none focus:border-blue-500"
                  />
                </div>
              }
            >
              {loadingAdmins ? (
                <Loading />
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="text-slate-500">
                      <tr>
                        <th className="border-b px-2 py-2 text-left">Nome</th>
                        <th className="border-b px-2 py-2 text-left">Tipo</th>
                        <th className="border-b px-2 py-2 text-left">Limite</th>
                        <th className="border-b px-2 py-2 text-left">POP</th>
                        <th className="border-b px-2 py-2 text-left">Estado</th>
                        <th className="border-b px-2 py-2 text-center">Acoes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminsFiltrados.map((user) => (
                        <tr key={user.idUsuario} className="hover:bg-slate-50">
                          <td className="border-b px-2 py-2 font-semibold text-slate-900">{user.name}</td>
                          <td className="border-b px-2 py-2">{user.tipousuario || user.tipoUsuario || "admin"}</td>
                          <td className="border-b px-2 py-2">
                            {user.tipousuario === "pleno" ? `${user.validadeusuario || 0} dias` : "Sem limite"}
                          </td>
                          <td className="border-b px-2 py-2">POP local</td>
                          <td className="border-b px-2 py-2">
                            <span className={`font-semibold ${user.estado === "ativo" ? "text-green-600" : "text-red-600"}`}>
                              {user.estado === "ativo" ? "Ativo" : "Desativado"}
                            </span>
                          </td>
                          <td className="border-b px-2 py-2">
                            <div className="flex flex-wrap justify-center gap-2">
                              <button
                                onClick={() => alternarAtivo(user.idUsuario)}
                                disabled={idAlternando === user.idUsuario}
                                className={`rounded px-3 py-1 text-white ${
                                  user.estado === "ativo"
                                    ? "bg-yellow-500 hover:bg-yellow-600"
                                    : "bg-green-500 hover:bg-green-600"
                                } disabled:opacity-60`}
                              >
                                {idAlternando === user.idUsuario
                                  ? "..."
                                  : user.estado === "ativo"
                                  ? "Desativar"
                                  : "Ativar"}
                              </button>
                              <button
                                onClick={() => removeAdmin(user.idUsuario)}
                                disabled={removingId === user.idUsuario}
                                className="rounded bg-red-600 px-3 py-1 text-white hover:bg-red-700 disabled:opacity-60"
                              >
                                {removingId === user.idUsuario ? "..." : "Remover"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {adminsFiltrados.length === 0 && (
                        <tr>
                          <td colSpan="6" className="py-4 text-center text-slate-500">
                            Nenhum admin encontrado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </DashboardPanel>

            <DashboardPanel
              title="Gestao de clientes"
              subtitle={
                MODO_AUDITORIA_SUPER
                  ? "Ativar, desconectar, congelar e adicionar dias aos clientes."
                  : "Ativar, desconectar, congelar, trocar plano, ocultar e criar recibo manualmente."
              }
              className="mt-4"
              action={
                <div className="flex flex-wrap gap-2">
                  {[
                    ["todos", "Todos"],
                    ["ativos", "Ativos"],
                    ["desativos", "Desativos"],
                    ...(MODO_AUDITORIA_SUPER ? [] : [["semRecibo", "Sem recibo"]]),
                    ["validade", "Validade"],
                    ...(MODO_AUDITORIA_SUPER ? [] : [["ocultos", "Ocultos"]]),
                    ["congelados", "Congelados"],
                  ].map(([valor, label]) => (
                    <button
                      key={valor}
                      onClick={() => setFiltroClientes(valor)}
                      className={`rounded border px-3 py-2 text-xs font-semibold ${
                        filtroClientes === valor
                          ? "border-blue-600 bg-blue-600 text-white"
                          : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              }
            >
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="border-b px-2 py-2 text-center">#</th>
                      <th className="border-b px-2 py-2 text-left">Cliente</th>
                      <th className="border-b px-2 py-2 text-left">Perfil visivel</th>
                      <th className="border-b px-2 py-2 text-left">Perfil real</th>
                      <th className="border-b px-2 py-2 text-left">Validade</th>
                      {!MODO_AUDITORIA_SUPER && (
                        <th className="border-b px-2 py-2 text-left">Recibos</th>
                      )}
                      <th className="border-b px-2 py-2 text-left">Estado</th>
                      <th className="border-b px-2 py-2 text-center">Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientesFiltrados.map((cliente, i) => {
                      const ativo = clienteEstaAtivo(cliente);
                      const semRecibo = clientesSemReciboIds.has(cliente.idUsuario);
                      const congelado = clienteEstaCongelado(cliente);
                      const oculto = cliente.estado === "oculto";
                      const dias = cliente.dias ?? diasRestantes(cliente);

                      return (
                        <tr key={cliente.idUsuario} className="hover:bg-slate-50">
                          <td className="border-b px-2 py-2 text-center">{i + 1}</td>
                          <td className="border-b px-2 py-2">
                            <div className="font-semibold text-slate-900">{cliente.name}</div>
                            <div className="text-slate-500">
                              {cliente.detalheNome || "Sem detalhes"} {cliente.contacto ? `- ${cliente.contacto}` : ""}
                            </div>
                          </td>
                          <td className="border-b px-2 py-2">{planoCliente(cliente)}</td>
                          <td className="border-b px-2 py-2">{perfilRealCliente(cliente) || "-"}</td>
                          <td className="border-b px-2 py-2">
                            {dias === null ? cliente.validadeusuario || "-" : dias < 0 ? "Expirado" : `${dias} dias`}
                          </td>
                          {!MODO_AUDITORIA_SUPER && (
                            <td className={`border-b px-2 py-2 ${semRecibo ? "font-semibold text-yellow-700" : ""}`}>
                              {cliente.totalRecibos || 0}
                            </td>
                          )}
                          <td className="border-b px-2 py-2">
                            <span className={`font-semibold ${ativo ? "text-green-600" : congelado ? "text-blue-700" : "text-red-600"}`}>
                              {congelado ? "Congelado" : ativo ? "Ativo" : "Desativo"}
                            </span>
                            {oculto && (
                              <span className="ml-2 rounded bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700">
                                Oculto
                              </span>
                            )}
                          </td>
                          <td className="border-b px-2 py-2">
                            <div className="flex flex-wrap justify-center gap-2">
                              <button
                                title="Detalhes"
                                onClick={() =>
                                  navigate(`/admin/clientes/ativos/detalhesusuario/${cliente.idUsuario}`)
                                }
                                className="rounded border border-slate-300 p-2 hover:bg-blue-50 hover:text-blue-600"
                              >
                                <InfoIcon size={16} />
                              </button>
                              {!MODO_AUDITORIA_SUPER && (
                                <>
                                  <button
                                    title="Recibos"
                                    onClick={() =>
                                      navigate(`/admin/clientes/ativos/recibosusuario/${cliente.idUsuario}`)
                                    }
                                    className="rounded border border-slate-300 p-2 hover:bg-blue-50 hover:text-blue-600"
                                  >
                                    <FileText size={16} />
                                  </button>
                                  <button
                                    title="Trocar perfil"
                                    onClick={() => trocarPerfilCliente(cliente)}
                                    disabled={acaoClienteId === `${cliente.idUsuario}-perfil`}
                                    className="rounded border border-slate-300 p-2 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-60"
                                  >
                                    {acaoClienteId === `${cliente.idUsuario}-perfil` ? (
                                      <LoaderCircle size={16} className="animate-spin" />
                                    ) : (
                                      <Pencil size={16} />
                                    )}
                                  </button>
                                </>
                              )}
                              <button
                                title="Adicionar dias"
                                onClick={() => adicionarDiasCliente(cliente)}
                                disabled={acaoClienteId === `${cliente.idUsuario}-dias`}
                                className="rounded border border-slate-300 p-2 hover:bg-yellow-50 hover:text-yellow-700 disabled:opacity-60"
                              >
                                {acaoClienteId === `${cliente.idUsuario}-dias` ? (
                                  <LoaderCircle size={16} className="animate-spin" />
                                ) : (
                                  <CalendarPlus size={16} />
                                )}
                              </button>
                              <button
                                title={congelado ? "Descongelar token" : "Congelar token"}
                                onClick={() => congelarOuDescongelarCliente(cliente)}
                                disabled={
                                  acaoClienteId === `${cliente.idUsuario}-congelar` ||
                                  acaoClienteId === `${cliente.idUsuario}-descongelar`
                                }
                                className="rounded border border-slate-300 p-2 hover:bg-sky-50 hover:text-sky-700 disabled:opacity-60"
                              >
                                {acaoClienteId === `${cliente.idUsuario}-congelar` ||
                                acaoClienteId === `${cliente.idUsuario}-descongelar` ? (
                                  <LoaderCircle size={16} className="animate-spin" />
                                ) : congelado ? (
                                  <Power size={16} />
                                ) : (
                                  <PowerOff size={16} />
                                )}
                              </button>
                              {!MODO_AUDITORIA_SUPER && (
                                <button
                                  title={oculto ? "Mostrar aos admins" : "Ocultar dos admins"}
                                  onClick={() => alterarVisibilidadeCliente(cliente)}
                                  disabled={acaoClienteId === `${cliente.idUsuario}-visibilidade`}
                                  className="rounded border border-slate-300 p-2 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-60"
                                >
                                  {acaoClienteId === `${cliente.idUsuario}-visibilidade` ? (
                                    <LoaderCircle size={16} className="animate-spin" />
                                  ) : oculto ? (
                                    <Eye size={16} />
                                  ) : (
                                    <EyeOff size={16} />
                                  )}
                                </button>
                              )}
                              {ativo ? (
                                <button
                                  title="Desconectar"
                                  onClick={() => alterarEstadoCliente(cliente, "desativo")}
                                  disabled={acaoClienteId === `${cliente.idUsuario}-desativo`}
                                  className="rounded border border-slate-300 p-2 hover:bg-red-50 hover:text-red-700 disabled:opacity-60"
                                >
                                  {acaoClienteId === `${cliente.idUsuario}-desativo` ? (
                                    <LoaderCircle size={16} className="animate-spin" />
                                  ) : (
                                    <PowerOff size={16} />
                                  )}
                                </button>
                              ) : !congelado ? (
                                <button
                                  title="Ativar"
                                  onClick={() => alterarEstadoCliente(cliente, "ativo")}
                                  disabled={acaoClienteId === `${cliente.idUsuario}-ativo`}
                                  className="rounded border border-slate-300 p-2 hover:bg-green-50 hover:text-green-700 disabled:opacity-60"
                                >
                                  {acaoClienteId === `${cliente.idUsuario}-ativo` ? (
                                    <LoaderCircle size={16} className="animate-spin" />
                                  ) : (
                                    <Power size={16} />
                                  )}
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {clientesFiltrados.length === 0 && (
                      <tr>
                        <td colSpan="8" className="py-4 text-center text-slate-500">
                          Nenhum cliente encontrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </DashboardPanel>
          </>
        )}
      </main>

      <div className="bg-blue-600">
        <Footer />
      </div>
    </div>
  );
}
